import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import * as adminService from '../../services/admin';

const RolePermissions = () => {
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [assignedPermissions, setAssignedPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState([]);

    useEffect(() => {
        loadRoles();
        loadPermissions();
    }, []);

    const loadRoles = async () => {
        try {
            const response = await adminService.listRoles();
            if (response.status === 'success') {
                setRoles(response.data);
            }
        } catch (error) {
            console.error('Error loading roles:', error);
            // toast.error('Failed to load roles');
        }
    };

    const loadPermissions = async () => {
        try {
            const response = await adminService.listPermissions();
            if (response.status === 'success') {
                setPermissions(response.data);
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            // toast.error('Failed to load permissions');
        }
    };

    const loadRolePermissions = async (roleId) => {
        try {
            setLoading(true);
            const response = await adminService.listRolePermissions(roleId);
            if (response.status === 'success') {
                setSelectedRole(response.data.role);
                setAssignedPermissions(response.data.assigned_permission_ids);
                setSelectedPermissions(response.data.assigned_permission_ids);
            }
        } catch (error) {
            console.error('Error loading role permissions:', error);
            // toast.error('Failed to load role permissions');
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = (e) => {
        const roleId = e.target.value;
        if (roleId) {
            loadRolePermissions(roleId);
        } else {
            setSelectedRole(null);
            setAssignedPermissions([]);
            setSelectedPermissions([]);
        }
    };

    const handlePermissionToggle = (permissionId) => {
        if (selectedPermissions.includes(permissionId)) {
            setSelectedPermissions(selectedPermissions.filter(id => id !== permissionId));
        } else {
            setSelectedPermissions([...selectedPermissions, permissionId]);
        }
    };

    const handleModuleToggle = (module) => {
        const modulePermissionIds = permissions[module].map(p => p.id);
        const allSelected = modulePermissionIds.every(id => selectedPermissions.includes(id));

        if (allSelected) {
            // Deselect all
            setSelectedPermissions(selectedPermissions.filter(id => !modulePermissionIds.includes(id)));
        } else {
            // Select all
            const newPermissions = [...new Set([...selectedPermissions, ...modulePermissionIds])];
            setSelectedPermissions(newPermissions);
        }
    };

    const handleSavePermissions = async () => {
        if (!selectedRole) {
            // toast.error('Please select a role');
            return;
        }

        try {
            setLoading(true);
            const response = await adminService.assignPermissionsToRole(selectedRole.id, {
                permission_ids: selectedPermissions
            });

            if (response.status === 'success') {
                // toast.success('Permissions assigned successfully');
                setAssignedPermissions(selectedPermissions);
            } else {
                // toast.error(response.message || 'Failed to assign permissions');
            }
        } catch (error) {
            console.error('Error saving permissions:', error);
            // toast.error('Failed to save permissions');
        } finally {
            setLoading(false);
        }
    };

    const isModuleFullySelected = (module) => {
        if (!permissions[module]) return false;
        const modulePermissionIds = permissions[module].map(p => p.id);
        return modulePermissionIds.every(id => selectedPermissions.includes(id));
    };

    const isModulePartiallySelected = (module) => {
        if (!permissions[module]) return false;
        const modulePermissionIds = permissions[module].map(p => p.id);
        const selectedCount = modulePermissionIds.filter(id => selectedPermissions.includes(id)).length;
        return selectedCount > 0 && selectedCount < modulePermissionIds.length;
    };

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fa fa-shield"></i> Role Permissions Management
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Select Role</label>
                            <select
                                className="form-select"
                                onChange={handleRoleChange}
                                disabled={loading}
                            >
                                <option value="">-- Select Role --</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>
                                        {role.role_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {selectedRole && (
                            <div className="col-md-6 d-flex align-items-end">
                                <button
                                    className="btn btn-success me-2"
                                    onClick={handleSavePermissions}
                                    disabled={loading}
                                >
                                    <i className="fa fa-save"></i> Save Permissions
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setSelectedPermissions(assignedPermissions)}
                                    disabled={loading}
                                >
                                    <i className="fa fa-undo"></i> Reset
                                </button>
                            </div>
                        )}
                    </div>

                    {selectedRole && (
                        <div className="permissions-grid">
                            <div className="alert alert-info">
                                <strong>Role:</strong> {selectedRole.role_name}
                                <span className="ms-3">
                                    <strong>Permissions Selected:</strong> {selectedPermissions.length}
                                </span>
                            </div>

                            {Object.keys(permissions).map(module => (
                                <div key={module} className="card mb-3">
                                    <div className="card-header bg-light">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`module-${module}`}
                                                checked={isModuleFullySelected(module)}
                                                ref={input => {
                                                    if (input) {
                                                        input.indeterminate = isModulePartiallySelected(module);
                                                    }
                                                }}
                                                onChange={() => handleModuleToggle(module)}
                                            />
                                            <label
                                                className="form-check-label fw-bold text-capitalize"
                                                htmlFor={`module-${module}`}
                                            >
                                                {module.replace(/_/g, ' ')}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            {permissions[module].map(permission => (
                                                <div key={permission.id} className="col-md-4 mb-2">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`perm-${permission.id}`}
                                                            checked={selectedPermissions.includes(permission.id)}
                                                            onChange={() => handlePermissionToggle(permission.id)}
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={`perm-${permission.id}`}
                                                        >
                                                            {permission.display_name}
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!selectedRole && (
                        <div className="text-center text-muted py-5">
                            <i className="fa fa-info-circle fa-3x mb-3"></i>
                            <p>Please select a role to manage permissions</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RolePermissions;
